import React, { useState, useEffect } from "react";
import { Box, Flex, useToast, Button, Text, Spacer, Select, Heading } from "@chakra-ui/react";
import { AiOutlineArrowLeft } from "react-icons/ai";

import preferencesService from "../../services/preferences.service";
import rulesService from "../../services/rules.service";
import countriesService from "../../services/countries.service";
import CountrySideList from "./CountrySideList";
import ColumnTransfer from "./ColumnTransfer";

const DEFAULT_KEY = "default";

// Resolve the merchantView for a country from the latest stored preferences,
// falling back to the default view when there's no per-country override.
const viewForSelected = (code) => {
  const prefs = JSON.parse(localStorage.getItem("preferences")) || {};
  if (code === DEFAULT_KEY) return [...(prefs.merchantView || [])];
  const byc = prefs.merchantViewByCountry || {};
  return [...(byc[code] && byc[code].length ? byc[code] : prefs.merchantView || [])];
};

const pinCountOf = (view) => view.reduce((c, i) => (i.pin === "left" ? c + 1 : c), 0);

function MerchantViewEdit(props) {
  const toast = useToast();
  const preferences = JSON.parse(localStorage.getItem("preferences"));
  const master = preferences.merchantViewMaster || [];
  const [pickerItems, setPickerItems] = useState([...master]);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_KEY);
  const [dbCountries, setDbCountries] = useState([]);
  const [merchantView, setMerchantView] = useState(viewForSelected(DEFAULT_KEY));
  const [merchantViewPinCount, setMerchantViewPinCount] = useState(pinCountOf(merchantView));
  const merchantViewPinColumn = [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }, { value: 5 }, { value: 6 }];

  // Merge in any rule columns (e.g. newly-added headings) not already in the master list.
  useEffect(() => {
    rulesService.getColumns().then((res) => {
      if (res && res.columns) {
        const existing = new Set(master.map((i) => i.value));
        const extra = res.columns.filter((c) => !existing.has(c)).map((c) => ({ value: c, label: c }));
        if (extra.length) setPickerItems([...master, ...extra]);
      }
    });
    countriesService.getCountries().then((res) => {
      if (res && res.countries) setDbCountries(res.countries);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMerchantView((mv) => mv.map((item, index) => ({ ...item, pin: index < merchantViewPinCount ? "left" : "" })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantViewPinCount]);

  const onCountryChange = (code) => {
    setSelectedCountry(code);
    const v = viewForSelected(code);
    setMerchantView(v);
    setMerchantViewPinCount(pinCountOf(v));
  };

  const countryItems = [{ code: DEFAULT_KEY, name: "Default" }, ...dbCountries];
  const isCustom = (code) => {
    const m = (JSON.parse(localStorage.getItem("preferences")) || {}).merchantViewByCountry || {};
    return !!m[code];
  };

  // Re-apply pins to the first N columns whenever the selection changes (add/remove/reorder).
  const applyPins = (view) => view.map((item, index) => ({ ...item, pin: index < merchantViewPinCount ? "left" : "" }));
  const onMerchantViewChange = (next) => setMerchantView(applyPins(next));

  const persist = (mutate) => {
    const prefs = JSON.parse(localStorage.getItem("preferences")) || {};
    mutate(prefs);
    localStorage.setItem("preferences", JSON.stringify(prefs));
  };

  const handleSave_merchantView = async () => {
    props.setisLoading(true);
    const payload =
      selectedCountry === DEFAULT_KEY
        ? { merchantView }
        : { ["merchantViewByCountry." + selectedCountry]: merchantView };
    const res = await preferencesService.update(payload);
    props.setisLoading(false);
    if (res && (res.warning || res.error)) {
      toast({ title: res.warning || res.error, position: "top", status: "error", isClosable: true });
      setMerchantView(viewForSelected(selectedCountry));
      return;
    }
    persist((prefs) => {
      if (selectedCountry === DEFAULT_KEY) {
        prefs.merchantView = merchantView;
      } else {
        prefs.merchantViewByCountry = prefs.merchantViewByCountry || {};
        prefs.merchantViewByCountry[selectedCountry] = merchantView;
      }
    });
    toast({ title: `Updated...!`, position: "top", status: "success", isClosable: true });
  };

  const handleResetCountry = async () => {
    props.setisLoading(true);
    const res = await preferencesService.update({ unset: ["merchantViewByCountry." + selectedCountry] });
    props.setisLoading(false);
    if (res && (res.warning || res.error)) {
      toast({ title: res.warning || res.error, position: "top", status: "error", isClosable: true });
      return;
    }
    persist((prefs) => {
      if (prefs.merchantViewByCountry) delete prefs.merchantViewByCountry[selectedCountry];
    });
    const v = viewForSelected(selectedCountry); // now falls back to default
    setMerchantView(v);
    setMerchantViewPinCount(pinCountOf(v));
    toast({ title: `Reset ${selectedCountry} to default`, position: "top", status: "success", isClosable: true });
  };

  return (
    <Box w="100%">
      <Flex alignItems="center" mb={3}>
        <Button size="xs" leftIcon={<AiOutlineArrowLeft />} variant="ghost" onClick={props.onBack}>
          Back to settings
        </Button>
        <Spacer />
        <Heading size="sm">Merchant view column preference</Heading>
        <Spacer />
      </Flex>
      <Flex align="flex-start">
        <CountrySideList items={countryItems} selected={selectedCountry} onSelect={onCountryChange} isCustom={isCustom} />
        <Box flex="1">
          {selectedCountry !== DEFAULT_KEY && (
            <Flex mb={2} alignItems="center">
              <Text fontSize="sm" color="gray.600">Editing the <b>{selectedCountry}</b> override</Text>
              <Spacer />
              <Button size="xs" variant="outline" colorScheme="red" onClick={handleResetCountry}>
                Reset to default
              </Button>
            </Flex>
          )}
          <ColumnTransfer items={pickerItems} selected={merchantView} onChange={onMerchantViewChange} />
          <Flex>
            <Button mr={3} size="sm" fontSize=".8rem" onClick={handleSave_merchantView} colorScheme="blue">
              Save
            </Button>
            <Button size="sm" fontSize=".8rem" mr={3} onClick={() => onMerchantViewChange([...pickerItems])}>
              Select all
            </Button>
            <Button size="sm" fontSize=".8rem" mr={3} onClick={() => setMerchantView([])}>
              Unselect all
            </Button>
            <Button size="sm" fontSize=".8rem" mr={3} onClick={() => setMerchantView(viewForSelected(selectedCountry))}>
              Discard changes
            </Button>
            <Spacer />
            <Box>
              <Flex>
                <Text fontSize="sm" p={1}># of columns to be panned</Text>
                <Select
                  size="sm"
                  width={20}
                  onChange={(e) => setMerchantViewPinCount(e.target.value)}
                  value={merchantViewPinCount}
                >
                  {merchantViewPinColumn.map((s, i) => (
                    <option key={i} value={s.value}>{s.value}</option>
                  ))}
                </Select>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

export default MerchantViewEdit;
